import React from "react";
import { ChordChartWithHover } from "./ChordChartWithHover";
import { HiOutlinePlus, HiOutlineTrash, HiOutlineXMark } from "react-icons/hi2";

export interface LineCommentDisplay {
  id: string;
  text: string;
  userName: string;
  userId: string;
  createdAt: string;
}

export interface PresentationLineChartProps {
  rawText: string;
  displayText: string;
  className?: string;
  chordClassName?: string;
  commentsEnabled?: boolean;
  commentsByLine: Record<number, LineCommentDisplay[]>;
  activeLineIndex: number | null;
  expandAll?: boolean;
  currentUserId?: string | null;
  canDeleteAny?: boolean;
  newCommentText: string;
  savingComment?: boolean;
  deletingCommentId?: string | null;
  addLabel?: string;
  placeholder?: string;
  savingLabel?: string;
  onLineBubbleClick: (lineIndex: number) => void;
  onCloseLine: () => void;
  onNewCommentChange: (value: string) => void;
  onAddComment: () => void;
  onDeleteComment: (commentId: string) => void;
}

/**
 * Chord chart with speech-bubble comments shown on the right of each line.
 * Expanding a line shows the full thread inline (no modal).
 */
export const PresentationLineChart: React.FC<PresentationLineChartProps> = ({
  rawText,
  displayText,
  className = "",
  chordClassName,
  commentsEnabled = false,
  commentsByLine,
  activeLineIndex,
  expandAll = false,
  currentUserId,
  canDeleteAny = false,
  newCommentText,
  savingComment = false,
  deletingCommentId = null,
  addLabel = "Add comment",
  placeholder = "Add a note for this line…",
  savingLabel = "Saving…",
  onLineBubbleClick,
  onCloseLine,
  onNewCommentChange,
  onAddComment,
  onDeleteComment,
}) => {
  const rawLines = rawText.split("\n");
  const displayLines = displayText.split("\n");
  const lineCount = Math.max(rawLines.length, displayLines.length);

  return (
    <div className="w-full text-left space-y-0.5">
      {Array.from({ length: lineCount }, (_, lineIndex) => {
        const line = displayLines[lineIndex] ?? "";
        const comments = commentsByLine[lineIndex] || [];
        const hasComments = comments.length > 0;
        const isActive = activeLineIndex === lineIndex;
        const showThread = isActive || (expandAll && hasComments);
        const latest = comments[comments.length - 1];

        return (
          <div
            key={lineIndex}
            data-line-index={lineIndex}
            className={`group break-inside-avoid rounded-lg ${
              isActive
                ? "bg-violet-500/[0.08] dark:bg-violet-400/[0.07] -mx-1 px-1 py-1"
                : "py-0.5"
            }`}
          >
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="min-w-0 flex-1">
                {line.length === 0 ? (
                  <div className={`font-mono leading-relaxed ${className}`}>
                    &nbsp;
                  </div>
                ) : (
                  <ChordChartWithHover
                    text={line}
                    className={`leading-relaxed break-words max-w-full hyphens-auto ${className}`}
                    chordClassName={chordClassName}
                  />
                )}
              </div>

              {commentsEnabled && (
                <button
                  type="button"
                  onClick={() => onLineBubbleClick(lineIndex)}
                  className={`relative shrink-0 max-w-[42%] sm:max-w-[38%] touch-manipulation transition-all duration-200 text-left ${
                    hasComments || isActive
                      ? "opacity-100"
                      : "opacity-40 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
                  }`}
                  aria-label={
                    hasComments
                      ? `${comments.length} comments on line ${lineIndex + 1}`
                      : `Add comment on line ${lineIndex + 1}`
                  }
                >
                  <span
                    className={`relative block rounded-2xl rounded-br-md border backdrop-blur-md px-2.5 py-1.5 shadow-[0_6px_18px_-8px_rgba(139,92,246,0.45)] ${
                      hasComments
                        ? "bg-gradient-to-br from-violet-500/20 via-fuchsia-400/15 to-indigo-500/20 border-violet-400/35 text-violet-900 dark:from-violet-400/30 dark:via-fuchsia-400/20 dark:to-indigo-500/25 dark:border-violet-200/30 dark:text-violet-50"
                        : "bg-black/[0.04] border-gray-300 text-gray-400 hover:border-violet-400/50 hover:text-violet-700 hover:bg-violet-500/10 dark:bg-white/[0.06] dark:border-white/15 dark:text-white/45 dark:hover:border-violet-300/40 dark:hover:text-violet-200 dark:hover:bg-violet-400/15"
                    } ${isActive ? "ring-2 ring-violet-400/40 dark:ring-violet-300/40" : ""}`}
                  >
                    {hasComments ? (
                      <span className="block">
                        <span className="block text-[11px] sm:text-xs leading-snug line-clamp-2 font-medium">
                          {latest.text}
                        </span>
                        {comments.length > 1 && (
                          <span className="mt-0.5 block text-[10px] text-violet-600/70 dark:text-violet-200/70">
                            +{comments.length - 1} more
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center w-5 h-5">
                        <HiOutlinePlus className="w-3.5 h-3.5" strokeWidth={2.2} />
                      </span>
                    )}
                  </span>
                  {(hasComments || isActive) && (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 -z-10 rounded-2xl bg-violet-400/15 blur-md"
                    />
                  )}
                </button>
              )}
            </div>

            {commentsEnabled && showThread && (
              <div className="mt-2 ml-0 sm:ml-2 pl-3 border-l-2 border-violet-400/40 dark:border-violet-400/30 space-y-2">
                {isActive && (
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] uppercase tracking-wide text-violet-600 dark:text-violet-300/80 font-medium">
                      Line {lineIndex + 1}
                    </p>
                    <button
                      type="button"
                      onClick={onCloseLine}
                      className="p-1 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-black/5 dark:text-white/40 dark:hover:text-white dark:hover:bg-white/10"
                      aria-label="Close"
                    >
                      <HiOutlineXMark className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="rounded-2xl rounded-bl-md border border-violet-300/40 dark:border-violet-300/20 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-indigo-500/10 dark:from-violet-500/20 dark:via-fuchsia-500/10 dark:to-indigo-500/10 px-3 py-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-violet-800 dark:text-violet-100">
                          {comment.userName}
                        </p>
                        <p className="text-[10px] text-gray-400 dark:text-white/35 mt-0.5">
                          {comment.createdAt
                            ? new Date(comment.createdAt).toLocaleString()
                            : ""}
                        </p>
                      </div>
                      {(comment.userId === currentUserId || canDeleteAny) && (
                        <button
                          type="button"
                          onClick={() => onDeleteComment(comment.id)}
                          disabled={deletingCommentId === comment.id}
                          className="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-black/5 dark:text-white/35 dark:hover:text-red-400 dark:hover:bg-white/10 disabled:opacity-50"
                          aria-label="Delete"
                        >
                          <HiOutlineTrash className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-gray-800 dark:text-white/90 mt-1.5 whitespace-pre-wrap leading-snug">
                      {comment.text}
                    </p>
                  </div>
                ))}

                {isActive && (
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <input
                      type="text"
                      value={newCommentText}
                      onChange={(e) => onNewCommentChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          onAddComment();
                        }
                      }}
                      placeholder={placeholder}
                      className="flex-1 min-w-0 px-3 py-2 rounded-xl bg-white dark:bg-white/5 border border-violet-300/40 dark:border-violet-300/25 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/35 text-sm focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400/40"
                    />
                    <button
                      type="button"
                      onClick={onAddComment}
                      disabled={savingComment || !newCommentText.trim()}
                      className="shrink-0 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-500/90 to-fuchsia-500/80 text-white text-sm font-semibold hover:from-violet-400 hover:to-fuchsia-400 disabled:opacity-50"
                    >
                      {savingComment ? savingLabel : addLabel}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PresentationLineChart;
